import {
  BreakpointsEnum,
  ButtonType,
  CaesarsButton,
  ContainerHeader,
  FormField,
  GenericContainer,
  LoadingSpinner,
  Toast,
  ToastMessageContentItemType,
  ToastType,
  isBreakpointUp,
  useBreakpoint,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useNavigation} from '@react-navigation/native';
import {Text, View} from 'native-base';
import React, {useState} from 'react';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';

import SuccessFooterBanner from 'components/SuccessFooterBanner';
import {Routes} from 'models/Navigation';
import {supportPhoneNumber} from 'models/supportPhoneNumber';
import {ExchangeTransactionType} from 'services/api/affiliate';
import {APICallState} from 'services/api/api';

import TransferRewardsCreditsItem from './TransferRewardsCreditsItem';
import {useTheme} from 'styled-components/native';

export interface TransferArgTypes {
  amount: string;
  type: ExchangeTransactionType;
}
export interface TransferRewardCreditsProps {
  /**
   * Caesars rewards account balance
   */
  mainBalance: number;
  /**
   * Affiliate partner account balance
   */
  localBalance: number;
  onTransferPress: (transfer: TransferArgTypes) => void;
  conversionRate: number;
  apiState: APICallState;
  apiErrorMsg: string;
}

const TransferRewardCredits = (props: TransferRewardCreditsProps) => {
  const {
    localBalance,
    mainBalance,
    onTransferPress,
    conversionRate,
    apiState,
    apiErrorMsg,
  } = props;
  const breakpoint = useBreakpoint();
  const isDesktop = isBreakpointUp(breakpoint, BreakpointsEnum.SM); // TODO: update naming, treating isDesktop as a reserved word for the imported hook
  const [transactionType, setTransactionType] = useState(
    ExchangeTransactionType.DEPOSIT
  );
  const [transferAmount, setTransferAmount] = useState<string | undefined>(
    undefined
  );
  const isDeposit = transactionType === ExchangeTransactionType.DEPOSIT;
  const theme = useTheme();
  const [inputError, setInputError] = useState('');
  const {navigate} = useNavigation();

  const handleOnTransferPress = () => {
    const transferAmountNumber = Number(transferAmount);
    if (
      conversionRate !== 1 &&
      transferAmount &&
      transferAmountNumber % conversionRate !== 0
    ) {
      return setInputError(
        `${conversionRate} Reward Credits = $1 in comps. Please transfer in ${conversionRate} Reward Credit increments.`
      );
    }
    const amount = isDeposit
      ? transferAmountNumber / conversionRate
      : transferAmountNumber;

    onTransferPress({type: transactionType, amount: amount.toString()});
    setInputError('');
  };

  return (
    <GenericContainer
      hasShadow={false}
      containerStyle={{
        maxWidth: 600,
        width: !isDesktop ? '100%' : undefined,
        minHeight: !isDesktop ? '100%' : undefined,
      }}>
      <ContainerHeader title="TRANSFER REWARD CREDITS" />
      <View h="16px" />
      {apiState === APICallState.LOADING ? (
        <LoadingSpinner />
      ) : apiState === APICallState.SUCCESS ? (
        <View>
          <SuccessFooterBanner
            msg="Your credits have been exchanged."
            isSuccess={true}
            shouldAnimate={false}
          />
          <View h="12px" />
          <CaesarsButton
            title="Return To Dashboard"
            onPress={() => navigate(Routes.Main.Home)}
          />
        </View>
      ) : (
        <>
          {(!!inputError || apiState === APICallState.ERROR) && (
            <Toast
              toast={
                inputError || apiErrorMsg
                  ? {
                      type: ToastType.DANGER,
                      messageContent: [
                        {
                          elementType: ToastMessageContentItemType.TEXT,
                          text: inputError || apiErrorMsg,
                        },
                      ],
                    }
                  : {
                      type: ToastType.DANGER,
                      messageContent: [
                        {
                          elementType: ToastMessageContentItemType.TEXT,
                          text: 'Sorry, we were unable to handle your request. Please try again. If the issue persists, please contact support at',
                        },
                        {
                          elementType: ToastMessageContentItemType.LINK,
                          text: `${supportPhoneNumber}`,
                          href: `tel:${supportPhoneNumber}`,
                        },
                      ],
                    }
              }
            />
          )}

          {isDeposit ? (
            <>
              <TransferRewardsCreditsItem
                label={'Caesars Rewards Local'}
                balance={localBalance}
                labelPrefix={'From: '}
              />
              <View h="12px" />
              <TransferRewardsCreditsItem
                label={'Caesars Rewards Main'}
                balance={mainBalance}
                labelPrefix={'To: '}
              />
            </>
          ) : (
            <>
              <TransferRewardsCreditsItem
                label={'Caesars Rewards Main'}
                balance={mainBalance}
                labelPrefix={'From: '}
              />
              <View h="12px" />
              <TransferRewardsCreditsItem
                label={'Caesars Rewards Local'}
                balance={localBalance}
                labelPrefix={'To: '}
              />
            </>
          )}
          <View h="12px" />
          <CaesarsButton
            buttonStyle={{width: 178}}
            title="Swap"
            onPress={() =>
              setTransactionType(
                isDeposit
                  ? ExchangeTransactionType.WITHDRAW
                  : ExchangeTransactionType.DEPOSIT
              )
            }>
            <View alignItems="center" justifyContent="center" mr="4px">
              <Icon
                color={theme.colors.white}
                name="exchange-alt"
                solid
                style={{transform: [{rotateZ: '90deg'}], fontSize: 14}}
              />
            </View>
          </CaesarsButton>
          <View h="32px" />
          <FormField
            px="16px"
            label="Transfer Amount"
            value={transferAmount}
            onChangeText={setTransferAmount}
            nativeID={''}
            testID={''}
            editable={true}
          />
          {/** NOTE: this only handles the case where conversion rate os greater than one */}
          {conversionRate !== 1 && (
            <Text mt="6px" fontSize="11px">
              {conversionRate} Reward Credits = $1 in comps.{'\n'}Please
              transfer in {conversionRate} Reward Credit increments.
            </Text>
          )}
          <View h="24px" />
          <CaesarsButton
            type={ButtonType.PRIMARY}
            title="Transfer Credits"
            onPress={handleOnTransferPress}
          />
        </>
      )}
    </GenericContainer>
  );
};

export default TransferRewardCredits;
